// Accessibility enhancements for contact forms: focus management, inline errors, and summary errors.
document.addEventListener("DOMContentLoaded", () => {
  const forms = document.querySelectorAll("form.isolate[data-contact-form-id]");
  const translations = window.contactFormTranslations || {};

  // When Shopify returns a server-side status message, move focus to announce it.
  const focusServerStatus = (form) => {
    const statusMessage = form.querySelector("[data-contact-form-status]");
    if (!statusMessage) return;

    if (!statusMessage.hasAttribute("tabindex")) {
      statusMessage.setAttribute("tabindex", "-1");
    }

    statusMessage.focus();
  };

  // Map browser validity states to localized, user-friendly messages.
  const getErrorMessage = (field) => {
    if (field.validity.valueMissing) {
      return translations.required || "This field is required.";
    }

    if (field.validity.typeMismatch) {
      if (field.type === "email")
        return (
          translations.invalidEmail || "Please enter a valid email address."
        );
      return translations.invalidValue || "Please enter a valid value.";
    }

    if (field.validity.patternMismatch) {
      return translations.pattern || "Please match the requested format.";
    }

    return (
      field.validationMessage ||
      translations.generic ||
      "Please correct this field."
    );
  };

  // Reuse an inline error element per field so aria-describedby stays stable.
  const ensureFieldErrorElement = (form, field) => {
    const fieldId = field.id;
    if (!fieldId) return null;

    const errorId = `${fieldId}-error`;
    let errorElement = form.querySelector(`#${errorId}`);
    if (errorElement) return errorElement;

    errorElement = document.createElement("small");
    errorElement.className = "contact__field-error";
    errorElement.id = errorId;
    errorElement.setAttribute("role", "alert");
    errorElement.hidden = true;

    const fieldWrapper = field.closest(".field");
    if (fieldWrapper) {
      fieldWrapper.appendChild(errorElement);
    }

    return errorElement;
  };

  // Show inline error UI and connect the field to it for assistive technology.
  const showFieldError = (form, field) => {
    if (!field || !field.id) return null;

    const errorElement = ensureFieldErrorElement(form, field);
    if (!errorElement) return null;

    const message = getErrorMessage(field);
    errorElement.textContent = message;
    errorElement.hidden = false;

    field.setAttribute("aria-invalid", "true");
    field.setAttribute("aria-describedby", errorElement.id);

    const fieldWrapper = field.closest(".field");
    if (fieldWrapper) {
      fieldWrapper.classList.add("field--with-error");
    }

    return message;
  };

  // Clear inline error UI and restore field state when input becomes valid.
  const clearFieldError = (form, field) => {
    if (!field || !field.id) return;

    const errorElement = ensureFieldErrorElement(form, field);
    if (errorElement) {
      errorElement.textContent = "";
      errorElement.hidden = true;
    }

    field.removeAttribute("aria-invalid");
    field.removeAttribute("aria-describedby");

    const fieldWrapper = field.closest(".field");
    if (fieldWrapper) {
      fieldWrapper.classList.remove("field--with-error");
    }
  };

  // Pull the visible label text so summary errors are understandable out of context.
  const getFieldLabel = (form, field) => {
    const label = form.querySelector(`label[for="${field.id}"]`);
    if (!label) return "This field";

    return (label.textContent || "This field").replace("*", "").trim();
  };

  // Validate a single field and keep visual/ARIA states in sync.
  const validateField = (form, field) => {
    if (!field.willValidate) return true;

    if (field.checkValidity()) {
      clearFieldError(form, field);
      return true;
    }

    showFieldError(form, field);
    return false;
  };

  // Validate all fields, render bottom summary links, and return invalid controls.
  const validateForm = (form) => {
    const summary = form.querySelector("[data-contact-form-error-summary]");
    const summaryTitle = form.querySelector(
      "[data-contact-form-error-summary-title]",
    );
    const summaryList = form.querySelector(
      "[data-contact-form-error-summary-list]",
    );
    const fields = form.querySelectorAll("input, textarea, select");

    const invalidFields = [];

    fields.forEach((field) => {
      if (!field.willValidate) return;

      const isValid = validateField(form, field);
      if (!isValid) {
        invalidFields.push(field);
      }
    });

    if (!summary || !summaryList || !summaryTitle) {
      return invalidFields;
    }

    if (!invalidFields.length) {
      summary.hidden = true;
      summaryTitle.hidden = true;
      summaryList.innerHTML = "";
      return invalidFields;
    }

    summaryList.innerHTML = "";
    invalidFields.forEach((field) => {
      const message = getErrorMessage(field);
      const label = getFieldLabel(form, field);

      const listItem = document.createElement("li");
      const link = document.createElement("a");
      link.className = "link";
      link.href = `#${field.id}`;
      link.textContent = `${label}: ${message}`;
      // Summary links move focus directly to the related invalid control.
      link.addEventListener("click", (event) => {
        event.preventDefault();
        field.focus();
      });

      listItem.appendChild(link);
      summaryList.appendChild(listItem);
    });

    summary.hidden = false;
    summaryTitle.hidden = false;

    return invalidFields;
  };

  forms.forEach((form) => {
    focusServerStatus(form);

    const summaryTitle = form.querySelector(
      "[data-contact-form-error-summary-title]",
    );
    if (summaryTitle) {
      summaryTitle.hidden = true;
    }

    // Keep inline errors responsive while users fix inputs.
    form.querySelectorAll("input, textarea, select").forEach((field) => {
      if (!field.willValidate) return;

      field.addEventListener("input", () => {
        if (field.checkValidity()) {
          clearFieldError(form, field);
        }
      });

      field.addEventListener("blur", () => {
        validateField(form, field);
      });
    });

    // On submit, block only when invalid and focus the first failing field.
    form.addEventListener("submit", (event) => {
      const invalidFields = validateForm(form);
      if (!invalidFields.length) return;

      event.preventDefault();
      invalidFields[0].focus();
    });
  });
});
